import { useRef } from 'react';

import { Button } from '@/shared/ui/Button';
import { PaperclipIcon, XIcon } from '@/shared/ui/icons';
import { formatBytes } from '@/shared/utils/format';

import type { OwnFile } from './outgoing';

/** «Adjuntar», with the hidden file input it opens and a line on what fits. */
export function AttachButton({
  onFiles,
  disabled,
  hint,
}: {
  onFiles: (list: FileList) => void;
  disabled: boolean;
  hint: string;
}) {
  const input = useRef<HTMLInputElement>(null);
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
      <Button
        variant="secondary"
        size="sm"
        icon={<PaperclipIcon size={14} />}
        onClick={() => input.current?.click()}
        disabled={disabled}
      >
        Adjuntar
      </Button>
      <span className="text-ink-4 text-[12.5px]">{hint}</span>
      <input
        ref={input}
        type="file"
        multiple
        className="sr-only"
        tabIndex={-1}
        aria-label="Ficheros para adjuntar"
        onChange={(event) => {
          if (event.target.files) onFiles(event.target.files);
          // The input keeps the last selection; cleared, the same file can be chosen again.
          event.target.value = '';
        }}
      />
    </div>
  );
}

/** The files the person attached, each with its state and «Quitar». */
export function OwnFilesList({
  files,
  onRemove,
  disabled,
}: {
  files: OwnFile[];
  onRemove: (key: string) => void;
  disabled: boolean;
}) {
  if (files.length === 0) return null;
  return (
    <ul className="flex flex-col gap-1 text-[14px]" aria-label="Tus adjuntos">
      {files.map((file) => (
        <li
          key={file.key}
          className="border-line-2 flex items-center justify-between gap-3 rounded-md border px-3 py-1.5"
        >
          <span className="min-w-0">
            <span className="text-ink break-all">{file.name}</span>{' '}
            <span className="text-ink-3 text-[12.5px]">
              {formatBytes(file.size)}
              {file.state === 'uploading' ? ' · subiendo…' : ''}
            </span>
            {file.state === 'failed' ? (
              <span role="alert" className="text-danger block text-[12.5px]">
                {file.error}
              </span>
            ) : null}
          </span>
          <Button
            variant="ghost"
            size="sm"
            aria-label={`Quitar ${file.name}`}
            onClick={() => onRemove(file.key)}
            disabled={disabled}
            icon={<XIcon size={14} />}
          >
            Quitar
          </Button>
        </li>
      ))}
    </ul>
  );
}
